import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Flex, Input, ModalShell, Text } from '@/components/ui';
import { useCreateAllowedEmail } from '@/api/admin';
import { getApiErrorKey } from '@/api/core';
import { useNotificationStore } from '@/store/notificationStore';
import { ROLE_ID } from '@packages/types/auth/roles';
import { i18nKeys } from '@/i18n/i18nKeyGen';

interface AdminInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Admin 신규 초대 모달 (SuperAdmin 전용).
 *
 * 기존 사용자를 승격시키는 대신 allowed_emails에 role_id=Admin으로 등록한다.
 * 초대받은 사람이 가입하면 회사에 속하지 않은 Admin 계정이 만들어진다.
 */
const AdminInviteModal: React.FC<AdminInviteModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation(['auth', 'common']);
  const { showSuccess, showError } = useNotificationStore();
  const createAllowedEmail = useCreateAllowedEmail();

  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState('');

  const resetAndClose = () => {
    setEmail('');
    setUserName('');
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      showError(i18nKeys.auth.pleaseEnterEmail);
      return;
    }

    createAllowedEmail.mutate(
      {
        email: trimmedEmail,
        user_name: userName.trim() || undefined,
        role_id: ROLE_ID.ADMIN,
        status: 'T',
      },
      {
        onSuccess: () => {
          showSuccess(i18nKeys.auth.adminInviteSent);
          resetAndClose();
        },
        onError: (error: unknown) => {
          showError(getApiErrorKey(error, i18nKeys.auth.adminInviteFailed));
        },
      },
    );
  };

  return (
    <ModalShell
      isOpen={isOpen}
      title={t(i18nKeys.auth.adminRegistration)}
      onClose={resetAndClose}
      size="sm"
      footer={
        <Flex gap={2} justify="end">
          <Button onClick={resetAndClose}>{t(i18nKeys.auth.cancel)}</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={createAllowedEmail.isPending}
          >
            {t(i18nKeys.auth.add)}
          </Button>
        </Flex>
      }
    >
      <form onSubmit={handleSubmit}>
        <Flex direction="column" gap={4}>
          <Flex direction="column" gap={1}>
            <Text size="sm" weight="medium">
              {t(i18nKeys.auth.email)}
            </Text>
            <Input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="admin@example.com"
              autoFocus
            />
          </Flex>

          <Flex direction="column" gap={1}>
            <Text size="sm" weight="medium">
              {t(i18nKeys.auth.name)}
            </Text>
            <Input value={userName} onChange={e => setUserName(e.target.value)} />
          </Flex>

          <Text size="xs" color="textSecondary">
            {t(i18nKeys.auth.adminInviteDescription)}
          </Text>
        </Flex>
      </form>
    </ModalShell>
  );
};

export default AdminInviteModal;
